/**
 * voice-v8 — Zwei Fassungen einer Antwort: eine zum Sehen, eine zum Hören
 * ══════════════════════════════════════════════════════════════════════
 *
 * Martin am 30.08.2026:
 *
 *   *„Ich glaube, dass die Ausgabe nicht immer das sein soll, was gelesen
 *   wird. Das muss man verifizieren und unterscheiden. Das eine ist eine
 *   Ausgabe, Information zum Spiel, eine Tabelle, und dann soll quasi eine
 *   textuelle, saubere Zusammenfassung gemacht werden, die dann das System
 *   vorliest: Eckdaten, Kernaspekte und so weiter."*
 *
 * ## Warum das mehr ist als ein Filter
 *
 * Am selben Tag habe ich den Vertonungs-Filter repariert (pre.2413): eine
 * Tabelle klingt seither nicht mehr kaputt. Sie wird aber immer noch Zeile
 * für Zeile vorgelesen — nur eben als Sätze. Bei einer Rechnung mit drei
 * Posten geht das; bei zwölf Zeilen hört niemand mehr zu.
 *
 * Eine Tabelle ist zum ANSEHEN gebaut: das Auge springt, vergleicht, kehrt
 * zurück. Das Ohr kann das nicht. Es braucht denselben Inhalt anders
 * geordnet — Summe zuerst, dann die Posten, die sie erklären.
 *
 * ## Warum die Sprechfassung VORNE steht
 *
 * ⚠️ Das ist die eigentliche Entwurfsentscheidung, und sie ist nicht
 * beliebig. Die Stimme spricht mit, WÄHREND die Antwort noch wächst — die
 * TTS-Schlange bekommt jeden Brocken sofort. Stünde die Zusammenfassung am
 * Ende, wäre die Tabelle längst gesprochen, bevor die Zusammenfassung
 * überhaupt existiert. Man müsste die Stimme bis zum Ende der Antwort
 * anhalten und damit genau die Sofortigkeit opfern, die den Sprachweg
 * ausmacht.
 *
 * Steht sie vorne, kostet sie nichts: die Stimme fängt sogar FRÜHER an, weil
 * sie nicht auf die erste Tabellenzeile warten muss.
 *
 * ## Was passiert, wenn das Modell die Marke weglässt
 *
 * Dann gilt exakt das Verhalten von vorher: es wird gesprochen, was
 * dasteht. Kein Sonderfall, kein Fehler, keine stumme Antwort. Das ist
 * Absicht — eine Neuerung, die bei Nichtbefolgung etwas kaputt macht, wäre
 * hier die falsche Wahl.
 */

export const STIMME_AUF = "::stimme::";
export const STIMME_ZU = "::/stimme::";

interface GeteilteAntwort {
  /** Was in der Blase steht — ohne Marken, ohne Sprechfassung. */
  anzeige: string;
  /**
   * Der Teil der Anzeige, der VOR der Sprechfassung stand.
   *
   * ⚠️ 19.09.2026, Ticket 2384. Das ist fast immer der Satz, den die Regel
   *    ERSTES WORT (#2094/#2107) vor jedem Werkzeugaufruf verlangt. Er wurde
   *    live bereits gesprochen, waehrend das Werkzeug lief — er gehoert also
   *    in die Sprechquelle, aber NICHT in die Sprechfassung.
   */
  vorspann: string;
  /** Der Teil der Anzeige NACH der Sprechfassung — die Langfassung. */
  rest: string;
  /**
   * Was gesprochen wird, oder `null`, wenn es keine eigene Fassung gibt
   * (dann gilt die Anzeige, wie bisher).
   */
  stimme: string | null;
  /** Ist die Sprechfassung vollständig? Im Strom zählt das. */
  stimmeFertig: boolean;
}

/**
 * Kann aus diesem Anfang noch die Eröffnungsmarke werden?
 *
 * ⚠️ Im Strom kommt der Text brockenweise. Der erste Brocken kann `::sti`
 * lauten — dann ist noch nicht entschieden, ob eine Sprechfassung folgt. Wer
 * hier zu früh entscheidet, spricht die Marke mit oder verschluckt den
 * Anfang. Solange der bisherige Text ein echtes Präfix der Marke ist, wird
 * gewartet.
 */
export function markeNochMoeglich(roh: string): boolean {
  const s = roh.trimStart();
  return s.length < STIMME_AUF.length && STIMME_AUF.startsWith(s);
}

/**
 * Die Antwort in Anzeige- und Sprechfassung trennen.
 *
 * Verträgt unvollständigen Text (Strom) genauso wie fertigen (gespeicherte
 * Nachricht, erneutes Vorlesen).
 */
export function teileAntwort(roh: string): GeteilteAntwort {
  const t = roh ?? "";
  const auf = t.indexOf(STIMME_AUF);
  if (auf < 0) {
    /*
     * Keine Sprechfassung. ⚠️ Trotzdem aufräumen: taucht die Schlussmarke
     * allein im Text auf, darf sie NIE in der Blase landen. Eine sichtbare
     * Steuermarke ist für Martin ein Fehler, egal wie sie dorthin kam.
     *
     * ⚠️ Und einen ANGEFANGENEN Markenanfang am Ende abschneiden: im Strom
     *    kommt „::stimme::" zeichenweise. Ohne das spräche die Stimme „::s"
     *    mit und müsste es danach zurücknehmen — der Zeiger in
     *    `sprechNachschub` verschluckt dann den Rest des Zuges.
     */
    const sauber = ohneMarken(ohneAngefangeneMarke(t, STIMME_AUF));
    return {
      anzeige: sauber,
      vorspann: "",
      rest: sauber,
      stimme: null,
      stimmeFertig: true,
    };
  }
  /*
   * ⚠️ 19.09.2026 — HIER STAND `startsWith`, UND DAS WAR DER FEHLER.
   *
   * Die Regel ZWEI FASSUNGEN verlangt die Sprechfassung „GANZ am Anfang".
   * Die Regel ERSTES WORT verlangt vor jedem Werkzeugaufruf einen Satz mit
   * Substanz. Sobald ein Werkzeug im Spiel ist, sind beide nicht zugleich
   * erfuellbar — das Modell schreibt folgerichtig erst den Vorspann.
   *
   * An der Datenbank gemessen: von 29 Antworten mit Sprechfassung trugen
   * 14 einen Vorspann davor. Bei fast der Haelfte fiel die Sprechfassung
   * also ersatzlos weg, und gesprochen wurde die ganze lange Antwort —
   * Martins „du wiederholst Texte oft zweimal" (Ticket 2384).
   */
  const vorspann = ohneMarken(t.slice(0, auf)).trimEnd();
  const start = auf + STIMME_AUF.length;
  const ende = t.indexOf(STIMME_ZU, start);
  if (ende < 0) {
    /*
     * Noch im Fluss: alles nach der Eröffnung ist bisher Sprechfassung —
     * ⚠️ ABER ohne einen bereits begonnenen Schluss.
     *
     * Von meinem eigenen Test gefunden (30.08.): Die Schlussmarke kommt
     * zeichenweise herein. Ohne diese Kürzung wächst die Sprechfassung um
     * `:`, `::`, `::/`, `::/s` … — und die Stimme spricht sie mit, bevor die
     * Marke vollständig ist. Danach schnappt der Text zurück, der Zeiger
     * steht zu weit vorn, und der nächste Nachschub verschluckt Zeichen.
     */
    return {
      anzeige: vorspann,
      vorspann,
      rest: "",
      stimme: ohneAngefangeneMarke(t.slice(start).trimStart(), STIMME_ZU),
      stimmeFertig: false,
    };
  }
  const rest = ohneMarken(t.slice(ende + STIMME_ZU.length)).trimStart();
  return {
    anzeige: [vorspann, rest].filter((s) => s.trim()).join("\n\n"),
    vorspann,
    rest,
    stimme: t.slice(start, ende).trim(),
    stimmeFertig: true,
  };
}

/**
 * Was wirklich gesprochen werden soll.
 *
 * Die Sprechfassung ist für Tabellen: das Ohr braucht die Kurzfassung.
 * Listen (nächste Zulassungsstelle, drei Adressen) sind der Inhalt —
 * die wegzulassen war die halbe Spur.
 */
/*
 * ⚠️ 2026-09-04 (Martin, mit Bild: *„Total komisch. Er sagt mir etwas anderes
 * als was hier steht. Wie kann ich das vorlesen lassen, was hier angeführt
 * wurde?"*)
 *
 * Hier stand: enthält die Anzeige eine Tabelle, wird NUR die Sprechfassung
 * gesprochen. Das war richtig gedacht — eine Tabelle vorzulesen ist
 * unerträglich — aber zu grob: `istNurZumAnsehen` schlägt schon bei ZWEI
 * Tabellenzeilen an, und dann fiel die GANZE Anzeige weg.
 *
 * In Martins Fall standen unter der Tabelle mehrere Absätze — „Was ich daraus
 * lese: Dein Problem ist nicht zu wenig Schlaf, sondern zu wenig Tiefe …" —
 * also genau die Einsicht, um die es ging. Sie wurde nie gesprochen.
 *
 * Der Auftrag ans Modell sagt es selbst: die Sprechfassung fasst die TABELLE
 * zusammen („die Einzelheiten stehen ja sichtbar in der Tabelle"), danach
 * folgt die normale Antwort. Der Text nach der Tabelle ist also NICHT in der
 * Sprechfassung enthalten — er gehört gesprochen.
 *
 * Jetzt fällt nur die Tabelle weg, nicht der Text darum herum.
 */
export function sprechQuelle(g: GeteilteAntwort): string {
  if (!g.stimme) return g.anzeige;
  /*
   * Der Vorspann zuerst. Er wurde live bereits gesprochen, waehrend das
   * Werkzeug lief — liesse man ihn hier weg, spraenge die Sprechquelle beim
   * Auftauchen der Marke zurueck, und der Zeiger in `sprechNachschub`
   * (app.ts) verschluckte fuer den Rest des Zuges alles.
   *
   * ⚠️ 19.09.2026, Ticket 2384 — hier stand `g.anzeige`. Seit die Anzeige
   *    den Vorspann mittraegt, waere er damit ZWEIMAL in der Sprechquelle.
   *    Deshalb `g.rest`: der Teil NACH der Sprechfassung.
   *
   * ⚠️ Der Rest bleibt drin, und das ist Absicht. Mein erster Anlauf hat ihn
   *    bei Antworten ohne Tabelle weggelassen — an den 29 echten Antworten
   *    mit Sprechfassung nachgemessen waeren das 7 von 29 gewesen, und zwei
   *    bestehende Waechter haben es zu Recht umgeworfen: eine Trefferliste
   *    („Baden, Wiener Neustadt, Moedling") und der Absatz einer
   *    Gespraechsantwort sind der INHALT, nicht der Schmuck. Dasselbe hatte
   *    am 04.09. schon einmal die Einsicht unter der Tabelle verschluckt.
   *    Dass die Langfassung ihre eigene Sprechfassung nicht nacherzaehlt,
   *    ist eine Sache des Auftrags an das Modell, nicht dieser Leitung.
   */
  return [g.vorspann, g.stimme, ohneTabellenzeilen(g.rest)]
    .filter((s) => s.trim())
    .join("\n\n");
}

/** Ist das eine Tabellenzeile — Inhalt oder Trennlinie? */
function istTabellenzeile(z: string): boolean {
  const t = z.trim();
  if (!t.includes("|")) return false;
  // Trennlinie wie |---|:--:| — enthaelt ausser | nur -, : und Leerzeichen.
  if (/^\|?[\s:|-]+\|?$/.test(t)) return true;
  return t.split("|").length > 2;
}

/**
 * Die Tabelle raus, der Rest bleibt sprechbar.
 *
 * ⚠️ Zeilenweise, nicht als Block: eine Tabelle steht mitten im Text, und
 * ein Blockschnitt haette den Absatz danach mitgenommen — genau der Fehler,
 * der hier behoben wird, nur eine Ebene tiefer.
 */
export function ohneTabellenzeilen(anzeige: string): string {
  const zeilen = (anzeige ?? "").split("\n");
  const behalten: string[] = [];
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i]!;
    if (istTabellenzeile(z)) continue;
    /*
     * ⚠️ Die Anmoderation faellt mit. „Hier die letzten sieben Tage:" kuendigt
     * etwas an, das gleich nicht kommt — gesprochen bleibt ein Versprechen
     * offen. Nur wenn die Zeile mit einem Doppelpunkt endet UND danach
     * (ueber Leerzeilen hinweg) wirklich eine Tabelle folgt.
     */
    if (/:\s*$/.test(z.trim())) {
      let j = i + 1;
      while (j < zeilen.length && zeilen[j]!.trim() === "") j++;
      if (j < zeilen.length && istTabellenzeile(zeilen[j]!)) continue;
    }
    behalten.push(z);
  }
  return behalten.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Nur echte Tabellen — Listen werden vorgelesen. */
export function istNurZumAnsehen(anzeige: string): boolean {
  const zeilen = (anzeige ?? "").split("\n");
  let tabellen = 0;
  for (const z of zeilen) {
    if (z.includes("|") && z.trim().split("|").length > 2) tabellen++;
  }
  return tabellen >= 2;
}

/** Jede Steuermarke raus — für die Anzeige, kompromisslos. */
export function ohneMarken(t: string): string {
  return (t ?? "")
    .split(STIMME_AUF)
    .join("")
    .split(STIMME_ZU)
    .join("")
    .replace(/\n{3,}/g, "\n\n");
}

/**
 * Einen angefangenen Markenanfang am Ende abschneiden.
 *
 * "Der Gast zahlt ::/st" -> "Der Gast zahlt " — die vier Zeichen sind noch
 * keine Marke, aber sie sind auch kein Text. Im Zweifel lieber ein Zeichen
 * später sprechen als eines zu viel.
 *
 * ⚠️ 19.09.2026: gilt seit Ticket 2384 fuer BEIDE Marken. Solange die
 *    Sprechfassung nur ganz vorne stehen durfte, reichte `markeNochMoeglich`
 *    fuer die Eroeffnung — es prueft den GANZEN bisherigen Text. Hinter
 *    einem Vorspann greift das nicht mehr, und „Das PDF ist da.::s" waere
 *    mitgesprochen worden.
 */
function ohneAngefangeneMarke(t: string, marke: string): string {
  const max = Math.min(marke.length - 1, t.length);
  for (let n = max; n > 0; n--) {
    if (marke.startsWith(t.slice(t.length - n))) return t.slice(0, t.length - n);
  }
  return t;
}
